// 🚀 App Principal - Plataforma Revolucionaria v3.0
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useEffect, useMemo } from 'react';
import { useAuth } from '@/lib/hooks';
import { useMenus } from '@/lib/hooks/useMenus';
import { useTranslation } from '@/lib/hooks/useTranslation';
import { AppShell } from '@/components/layouts';
import { ModoAppShell, type OpcionApp } from '@/components/layouts/ModoAppShell';
import { Login } from '@/features/auth/Login';
import { marca } from '@brand';
import { ResetClave } from '@/features/auth/ResetClave';
import { loginSlugStore } from '@/lib/api/auth.api';
import { useTabsStore } from '@/stores/tabs.store';
import type { Tab } from '@/stores/tabs.store';
import { PermissionAssignment } from '@/features/permission-assignment/PermissionAssignment';
import { MiCuenta } from '@/features/cuenta/MiCuenta';
import { Empresas } from '@/features/admin/Empresas';
import { Paises } from '@/features/admin/Paises';
import { ArquitecturaSistema } from '@/features/admin/ArquitecturaSistema';
import { AyudaSistemas } from '@/features/admin/AyudaSistemas';
import { AsistenteConfig } from '@/features/admin/AsistenteConfig';
import { Respaldos } from '@/features/admin/Respaldos';
import { Prospectos } from '@/features/crm/Prospectos';
import { Salon } from '@/features/pos/Salon';
import { RestDashboard } from '@/features/pos/RestDashboard';
import { MeseroMovil } from '@/features/pos/MeseroMovil';
import { MesasRestaurant } from '@/features/pos/MesasRestaurant';
import { KdsRestaurant } from '@/features/pos/KdsRestaurant';
import { EstacionesRestaurant } from '@/features/pos/EstacionesRestaurant';
import { MeserosRestaurant } from '@/features/pos/MeserosRestaurant';
import { ConfigRestaurant } from '@/features/pos/ConfigRestaurant';
import { ReportesRestaurant } from '@/features/pos/ReportesRestaurant';
import { ReservasRestaurant } from '@/features/pos/ReservasRestaurant';
import { RecetasRestaurant } from '@/features/pos/RecetasRestaurant';
import { FotosMenu } from '@/features/pos/FotosMenu';
import { SignagePreview } from '@/features/signage/SignagePreview';
import { SignageTV } from '@/features/signage/SignageTV';
import { CertificadoSignage } from '@/features/signage/CertificadoSignage';
import { SignagePair } from '@/features/signage/SignagePair';
import { Pantallas as SignagePantallas } from '@/features/signage/Pantallas';
import { Plantillas as SignagePlantillas } from '@/features/signage/Plantillas';
import { Programacion as SignageProgramacion } from '@/features/signage/Programacion';
import { Grupos as SignageGrupos } from '@/features/signage/Grupos';
import { Temas as SignageTemas } from '@/features/signage/Temas';
import { DashboardSignage } from '@/features/signage/Dashboard';
import { Campanas as SignageCampanas } from '@/features/signage/Campanas';
import { AyudaSignage } from '@/features/signage/AyudaSignage';
import { AlertasSignage } from '@/features/signage/Alertas';
import { Uplift as SignageUplift } from '@/features/signage/Uplift';
import { Reglas as SignageReglas } from '@/features/signage/Reglas';
import { Anuncios as SignageAnuncios } from '@/features/signage/Anuncios';
import { Phygital as SignagePhygital } from '@/features/signage/Phygital';
import { Medios as SignageMedios } from '@/features/signage/Medios';
import { Experimentos as SignageExperimentos } from '@/features/signage/Experimentos';
import { FeedsFuentes as SignageFeeds } from '@/features/signage/FeedsFuentes';
import { Anunciantes as SignageAnunciantes } from '@/features/signage/Anunciantes';
import { Contratos as SignageContratos } from '@/features/signage/Contratos';
import { Combos as SignageCombos } from '@/features/signage/Combos';
import { Cupones as SignageCupones } from '@/features/signage/Cupones';
import { Tarifario as SignageTarifario } from '@/features/signage/Tarifario';
import { AvisosLocal as SignageAvisos } from '@/features/signage/Avisos';
import { SignageSocial } from '@/features/signage/Social';
import { SignageBuzon } from '@/features/signage/Buzon';
import { Salud as SignageSalud } from '@/features/signage/Salud';
import { PedidosExternosRestaurant } from '@/features/pos/PedidosExternosRestaurant';
import { CartaQr } from '@/features/pos/CartaQr';
import { AyudaRestaurant } from '@/features/pos/AyudaRestaurant';
import { DashboardWeb } from '@/features/ecommerce/DashboardWeb';
import { PublicacionWeb } from '@/features/ecommerce/PublicacionWeb';
import { ColeccionesWeb, BannersWeb, PaginasWeb } from '@/features/ecommerce/ContenidoWeb';
import { PedidosWeb, CarritosWeb } from '@/features/ecommerce/PedidosWeb';
import { ConfigTienda, PagosWeb, EnviosWeb } from '@/features/ecommerce/ConfigWeb';
import { TiendaPublica } from '@/features/ecommerce/TiendaPublica';
import { Contratos as ContratosCrm } from '@/features/crm/Contratos';
import { BandejaWhatsApp } from '@/features/crm/BandejaWhatsApp';
import { VerificarContratoCrm } from '@/features/crm/VerificarContratoCrm';
import { VerificarReceta } from '@/features/medical/VerificarReceta';
import { VerificarPedido } from '@/features/medical/VerificarPedido';
import { Usuarios } from '@/features/admin/Usuarios';
import { Perfiles } from '@/features/admin/Perfiles';
import { Procesos } from '@/features/admin/Procesos';
import { Menus } from '@/features/admin/Menus';
import { Submenus } from '@/features/admin/Submenus';
import { Opciones } from '@/features/admin/Opciones';
import { Partner } from '@/features/admin/Partner';
import { RetencionCodigos } from '@/features/admin/RetencionCodigos';
import { AvisoPrivacidad, AvisoPrivacidadGate } from '@/features/admin/AvisoPrivacidad';
import { Brechas } from '@/features/admin/Brechas';
import { RegistroTratamiento } from '@/features/admin/RegistroTratamiento';
import { RetencionDatos } from '@/features/admin/RetencionDatos';
import { Delegado } from '@/features/admin/Delegado';
import { ConsolaSQL } from '@/features/admin/ConsolaSQL';
import { DocumentosRecibidosSri } from '@/features/sri/DocumentosRecibidosSri';
import { DashboardSri } from '@/features/sri/DashboardSri';
import { ProcesarRecibidos, ProcesarEmitidos } from '@/features/sri/ProcesarDocumentos';
import { DocumentosEmitidosSri } from '@/features/sri/DocumentosEmitidosSri';
import { CxcFacturas, CxpFacturas } from '@/features/financiero/CarteraFacturas';
import { RegistrarCompra } from '@/features/financiero/RegistrarCompra';
import { LiquidacionCompra } from '@/features/financiero/LiquidacionCompra';
import { PrefacturasProveedor } from '@/features/financiero/PrefacturasProveedor';
import { AyudaFinanciero } from '@/features/financiero/AyudaFinanciero';
import { AyudaSunatPe } from '@/features/financiero/AyudaSunatPe';
import { TvDispositivos } from '@/features/admin/TvDispositivos';
import { TvPlantillas } from '@/features/admin/TvPlantillas';
import { TvMedios } from '@/features/admin/TvMedios';
import { TvSignage } from '@/features/tv/TvSignage';
import { VerificarContrato } from '@/features/nomina/VerificarContrato';
import { SolicitudesArco } from '@/features/nomina/SolicitudesArco';
import { Ajustes } from '@/features/nomina/Ajustes';
import { CxcNotasCredito, CxpNotasCredito } from '@/features/financiero/AplicarNotasCredito';
import { CxcAnticipos, CxpAnticipos } from '@/features/financiero/Anticipos';
import { CxcRetenciones, CxpRetenciones } from '@/features/financiero/AplicarRetenciones';
import { FacturacionEspecializada } from '@/features/financiero/FacturacionEspecializada';
import { Facturacion } from '@/features/financiero/Facturacion';
import { NotasCredito } from '@/features/financiero/NotasCredito';
import { NotasDebito } from '@/features/financiero/NotasDebito';
import { Cotizaciones } from '@/features/financiero/Cotizaciones';
import { Retenciones } from '@/features/financiero/Retenciones';
import { PlanCuentas } from '@/features/contabilidad/PlanCuentas';
import { Asientos } from '@/features/contabilidad/Asientos';
import { LibroMayor, Comprobacion, EstadoSituacion, EstadoResultados, EstadoResultadosNIIF18, GastosPorNaturaleza, LibroDiario, FlujoEfectivo as FlujoEfectivoContab, CambiosPatrimonio, MayorTerceros } from '@/features/contabilidad/Reportes';
import { AyudaContabilidad } from '@/features/contabilidad/AyudaContabilidad';
import { Tributario } from '@/features/contabilidad/Tributario';
import { ATS } from '@/features/contabilidad/ATS';
import { MPM } from '@/features/contabilidad/MPM';
import { AyudaNIIF18 } from '@/features/contabilidad/AyudaNIIF18';
import { DashboardContabilidad } from '@/features/contabilidad/DashboardContabilidad';
import { ConfigContable } from '@/features/contabilidad/ConfigContable';
import { CentrosCosto, Proyectos, ResultadoAnalitico } from '@/features/contabilidad/Analitica';
import { PeriodosCierre } from '@/features/contabilidad/PeriodosCierre';
import { BI } from '@/features/contabilidad/BI';
import { Presupuesto } from '@/features/contabilidad/Presupuesto';
import { BIEnvios } from '@/features/contabilidad/BIEnvios';
import { CorreoContabilidad } from '@/features/contabilidad/CorreoContabilidad';
import { ReporteAging, ReporteEstadoCuenta } from '@/features/financiero/ReportesCartera';
import { ReporteRetenciones } from '@/features/financiero/ReporteRetenciones';
import { BandejaEmitidos } from '@/features/financiero/BandejaEmitidos';
import { ConciliacionEmitidos } from '@/features/financiero/ConciliacionEmitidos';
import { DashboardFinanciero } from '@/features/financiero/DashboardFinanciero';
import { Prorrateo } from '@/features/contabilidad/Prorrateo';
import { AsientosRecurrentes } from '@/features/contabilidad/AsientosRecurrentes';
import { ConfiguracionEmision } from '@/features/financiero/ConfiguracionEmision';
import { ComunicacionBaja } from '@/features/financiero/ComunicacionBaja';
import { CategoriasDocumento } from '@/features/financiero/CategoriasDocumento';
import { MisionDashboard, MisionAgendar, MisionMensaje, MisionReporte } from '@/features/mision/Mision';
import { MisPacientes } from '@/features/mision/MisPacientes';
import { MisionReporteFacturacion } from '@/features/mision/ReporteFacturacion';
import { TradingDashboard } from '@/features/trading/Dashboard';
import { TradingTransacciones } from '@/features/trading/Transacciones';
import { TradingBinance } from '@/features/trading/Binance';
import { AyudaTrading } from '@/features/trading/AyudaTrading';
import { Categorias } from '@/features/inventario/Categorias';
import { DashboardInventario } from '@/features/inventario/DashboardInventario';
import { AyudaInventario } from '@/features/inventario/AyudaInventario';
import { Productos } from '@/features/inventario/Productos';
import { Atributos } from '@/features/inventario/Atributos';
import { Ubicaciones } from '@/features/inventario/Ubicaciones';
import { Reubicaciones } from '@/features/inventario/Reubicaciones';
import { TomaFisica } from '@/features/inventario/TomaFisica';
import { Reservas } from '@/features/inventario/Reservas';
import { CierrePeriodo } from '@/features/inventario/CierrePeriodo';
import { CostosInventario } from '@/features/inventario/CostosInventario';
import { Reposicion } from '@/features/inventario/Reposicion';
import { PreciosCompra } from '@/features/inventario/PreciosCompra';
import { AbcProductos } from '@/features/inventario/AbcProductos';
import { MarkdownLiquidacion } from '@/features/inventario/MarkdownLiquidacion';
import { StockUbicaciones } from '@/features/inventario/StockUbicaciones';
import { RentabilidadConfig } from '@/features/inventario/RentabilidadConfig';
import { OrdenesTrabajo } from '@/features/produccion/OrdenesTrabajo';
import { RecetasProduccion } from '@/features/produccion/Recetas';
import { RecetasCatering } from '@/features/catering/Recetas';
import { DashboardCatering } from '@/features/catering/Dashboard';
import { AyudaCatering } from '@/features/catering/Ayuda';
import { PisoCatering } from '@/features/catering/Piso';
import { AndonCatering } from '@/features/catering/Andon';
import { AndonTv } from '@/features/catering/AndonTv';
import { DashboardHotel } from '@/features/hotel/Dashboard';
import { RackHotel } from '@/features/hotel/Rack';
import { ReservasHotel } from '@/features/hotel/Reservas';
import { HousekeepingHotel } from '@/features/hotel/Housekeeping';
import { TableroTaller } from '@/features/taller/TableroTaller';
import { OrdenesTaller } from '@/features/taller/OrdenesTaller';
import { RecepcionTaller } from '@/features/taller/RecepcionTaller';
import { CatalogoTaller } from '@/features/taller/CatalogoTaller';
import { ConfigTaller } from '@/features/taller/ConfigTaller';
import { AyudaTaller } from '@/features/taller/AyudaTaller';
import { PitStop } from '@/features/taller/PitStop';
import { MiBanco } from '@/features/taller/MiBanco';
import { Pasaporte } from '@/features/taller/Pasaporte';
import { InformesTaller } from '@/features/taller/InformesTaller';
import { RetirosTaller } from '@/features/taller/RetirosTaller';
import { PlanesTaller } from '@/features/taller/PlanesTaller';
import { UsadosTaller } from '@/features/taller/UsadosTaller';
import { ComisionesTaller } from '@/features/taller/ComisionesTaller';
import { CajaCobrosTaller } from '@/features/taller/CajaCobrosTaller';
import { CajaTaller } from '@/features/taller/CajaTaller';
import { FacturasTaller } from '@/features/taller/FacturasTaller';
import { PerfilesTaller } from '@/features/taller/PerfilesTaller';
import { VerificarBench } from '@/features/taller/VerificarBench';
import { TvTaller } from '@/features/taller/TvTaller';
import { MiDiaHotel } from '@/features/hotel/MiDia';
import { MantenimientoHotel } from '@/features/hotel/Mantenimiento';
import { CierreNocturnoHotel } from '@/features/hotel/CierreNocturno';
import { TarifasHotel } from '@/features/hotel/Tarifas';
import { CatalogoHotel } from '@/features/hotel/Catalogo';
import { ConfigHotel } from '@/features/hotel/ConfigHotel';
import { AyudaHotel } from '@/features/hotel/AyudaHotel';
import { FeriadosHotel } from '@/features/hotel/FeriadosHotel';
import { ParteHuespedes } from '@/features/hotel/ParteHuespedes';
import { GruposHotel } from '@/features/hotel/Grupos';
import { ReportesHotel } from '@/features/hotel/Reportes';
import { CajaRecepcion } from '@/features/hotel/CajaRecepcion';
import { RepartoApp } from '@/features/catering/RepartoApp';
import { Kiosco } from '@/features/catering/Kiosco';
import { Comedor } from '@/features/catering/Comedor';
import { CadenaCert } from '@/features/catering/CadenaCert';
import { EstacionesCatering } from '@/features/catering/Estaciones';
import { CapacidadCatering } from '@/features/catering/Capacidad';
import { CalibracionCatering } from '@/features/catering/Calibracion';
import { CalendarioCatering } from '@/features/catering/Calendario';
import { AbastecimientoCatering } from '@/features/catering/Abastecimiento';
import { ComensalesCatering } from '@/features/catering/Comensales';
import { OpcionesCatering } from '@/features/catering/Opciones';
import { ConsumoCatering } from '@/features/catering/Consumo';
import { PrescripcionesCatering } from '@/features/catering/Prescripciones';
import { CadenaFrioCatering } from '@/features/catering/CadenaFrio';
import { FlotaCatering } from '@/features/catering/Flota';
import { EsgCatering } from '@/features/catering/Esg';
import { MenusCatering } from '@/features/catering/Menus';
import { ProduccionCatering } from '@/features/catering/Produccion';
import { ContratosCatering } from '@/features/catering/Contratos';
import { CortesCatering } from '@/features/catering/Cortes';
import { CosteoCatering } from '@/features/catering/Costeo';
import { RutasCatering } from '@/features/catering/Rutas';
import { DespachoCatering } from '@/features/catering/Despacho';
import { MenajeCatering } from '@/features/catering/Menaje';
import { NutricionCatering } from '@/features/catering/Nutricion';
import { DietasCatering } from '@/features/catering/Dietas';
import { PronosticoCatering } from '@/features/catering/Pronostico';
import { HaccpCatering } from '@/features/catering/Haccp';
import { LicitacionCatering } from '@/features/catering/Licitacion';
import { OptimizadorCatering } from '@/features/catering/Optimizador';
import { IngenieriaCatering } from '@/features/catering/Ingenieria';
import { Marcas } from '@/features/inventario/Marcas';
import { MotivosAjuste } from '@/features/inventario/MotivosAjuste';
import { ListasPrecios } from '@/features/inventario/ListasPrecios';
import { Precios } from '@/features/inventario/Precios';
import { Bodegas } from '@/features/inventario/Bodegas';
import { PermisosBodega } from '@/features/inventario/PermisosBodega';
import { Recepcion } from '@/features/inventario/Recepcion';
import { Transferencias } from '@/features/inventario/Transferencias';
import { TransferenciasConfig } from '@/features/inventario/TransferenciasConfig';
import { Kardex } from '@/features/inventario/Kardex';
import { Valorizacion } from '@/features/inventario/Valorizacion';
import { StockActual, Caducidades, BajoMinimo } from '@/features/inventario/Reportes';
import { AjusteInventario, CargaInicial } from '@/features/inventario/Ajustes';
import { Documentos } from '@/features/inventario/Documentos';
import { Reempaque } from '@/features/inventario/Reempaque';
import { ClasificacionVentas } from '@/features/inventario/ClasificacionVentas';
import { CodigosProducto } from '@/features/inventario/CodigosProducto';
import { Cajas } from '@/features/pos/Cajas';
import { ConfiguracionBalanza } from '@/features/pos/ConfiguracionBalanza';
import { FormasPago } from '@/features/pos/FormasPago';
import { Denominaciones } from '@/features/pos/Denominaciones';
import { EnConstruccion } from '@/features/pos/shared';
import { Empleados } from '@/features/nomina/Empleados';
import { Contratos } from '@/features/nomina/Contratos';
import { Departamentos } from '@/features/nomina/Departamentos';
import { Cargos as CargosNomina } from '@/features/nomina/Cargos';
import { Novedades } from '@/features/nomina/Novedades';
import { Anticipos } from '@/features/nomina/Anticipos';
import { Desprendible } from '@/features/nomina/Desprendible';
import { Prestamos } from '@/features/nomina/Prestamos';
import { PartesLabor } from '@/features/nomina/PartesLabor';
import { Parametros as ParametrosNomina } from '@/features/nomina/Parametros';
import { Rubros } from '@/features/nomina/Rubros';
import { GenerarRol, HistorialRoles } from '@/features/nomina/Rol';
import { RolSemanal, RolBisemanal } from '@/features/nomina/RolSemanal';
import { Pagos as PagosNomina } from '@/features/nomina/Pagos';
import { DecimoTercero, DecimoCuarto, Utilidades } from '@/features/nomina/Beneficios';
import { FondosReserva } from '@/features/nomina/FondosReserva';
import { Vacaciones } from '@/features/nomina/Vacaciones';
import { Liquidaciones } from '@/features/nomina/Liquidaciones';
import { PlanillasIess } from '@/features/nomina/PlanillasIess';
import { PrestamosIess } from '@/features/nomina/PrestamosIess';
import { ImpuestoRenta } from '@/features/nomina/ImpuestoRenta';
import { RepRolGeneral, RepProvisiones, RepCostoLaboral } from '@/features/nomina/ReportesNomina';
import { SolicitudesPortal } from '@/features/nomina/SolicitudesPortal';
import { MisRoles, MisVacaciones, MisSolicitudes, MisDatos, MiAsistencia } from '@/features/nomina/PortalEmpleado';
import { DashboardNomina } from '@/features/nomina/DashboardNomina';
import { DashboardControlBancario } from '@/features/control-bancario/DashboardControlBancario';
import { Bancos as BancosCB, Cuentas as CuentasCB, Posicion as PosicionCB } from '@/features/control-bancario/BancosCuentas';
import { ParametrosCB, ReglasConciliacion, FormatosExtracto } from '@/features/control-bancario/Configuracion';
import { Movimientos as MovimientosCB } from '@/features/control-bancario/Movimientos';
import { PagosLote } from '@/features/control-bancario/PagosLote';
import { TiposCambio } from '@/features/financiero/TiposCambio';
import { ExcepcionesContables } from '@/features/contabilidad/Excepciones';
import { CuadreControl } from '@/features/contabilidad/Cuadre';
import { TableroTributario } from '@/features/contabilidad/TableroTributario';
import { AuditoriaContinua } from '@/features/contabilidad/Auditoria';
import { PistaAuditoria } from '@/features/admin/PistaAuditoria';
import { PaqueteAuditor } from '@/features/contabilidad/PaqueteAuditor';
import { AnaliticaAuditoria } from '@/features/contabilidad/AnaliticaAuditoria';
import { Transferencias as TransferenciasCB } from '@/features/control-bancario/Transferencias';
import { ImportarExtractos } from '@/features/control-bancario/ImportarExtractos';
import { ConciliacionAutomatica, ConciliacionManual, Partidas, HistorialConciliaciones } from '@/features/control-bancario/Conciliacion';
import { Chequeras, EmisionCheques, ChequesGirados, AnulacionCheques } from '@/features/control-bancario/Cheques';
import { CajaFondos, CajaVales, CajaReposicion, CajaArqueo } from '@/features/control-bancario/CajaChica';
import { ProgramacionPagos, OrdenesPago, AprobacionPagos, Cobros, FlujoDeCaja } from '@/features/control-bancario/Tesoreria';
import { LibroBancos, ReporteConciliacion, ReporteCheques, FlujoEfectivo } from '@/features/control-bancario/Reportes';
import { AyudaControlBancario } from '@/features/control-bancario/AyudaControlBancario';
import { MapeoContableCB } from '@/features/control-bancario/MapeoContable';
import { SmtpControlBancario } from '@/features/control-bancario/SmtpControlBancario';
import { ActivosAF } from '@/features/activo-fijo/Activos';
import { DepreciacionAF } from '@/features/activo-fijo/Depreciacion';
import { PeriodosAF } from '@/features/activo-fijo/PeriodosAF';
import { MapeoAF } from '@/features/activo-fijo/MapeoAF';
import { BajasAF } from '@/features/activo-fijo/Bajas';
import { VentasAF } from '@/features/activo-fijo/Ventas';
import { RegistroAF, MovimientoAF, DepreciadosAF, DistribucionAF, ProyeccionAF, ConciliacionAF, FiscalAF } from '@/features/activo-fijo/Reportes';
import { DashboardAF } from '@/features/activo-fijo/DashboardAF';
import { AyudaActivoFijo } from '@/features/activo-fijo/AyudaActivoFijo';
import { EtiquetasAF } from '@/features/activo-fijo/Etiquetas';
import { TomaFisicaAF } from '@/features/activo-fijo/TomaFisica';
import { ConciliacionTomaAF } from '@/features/activo-fijo/ConciliacionToma';
import { RevalorizacionAF } from '@/features/activo-fijo/Revalorizacion';
import { ImpuestoDiferidoAF } from '@/features/activo-fijo/ImpuestoDiferido';
import { CapitalizarAF } from '@/features/activo-fijo/Capitalizar';
import { RevisionVidaUtilAF } from '@/features/activo-fijo/RevisionVidaUtil';
import { ArrendamientosAF } from '@/features/activo-fijo/Arrendamientos';
import { DeterioroAF } from '@/features/activo-fijo/Deterioro';
import { PlacasAF } from '@/features/activo-fijo/Placas';
import { MejorasAF } from '@/features/activo-fijo/Mejoras';
import { ComponentesAF } from '@/features/activo-fijo/Componentes';
import { CargaInicialAF } from '@/features/activo-fijo/CargaInicial';
import { TransferenciasAF } from '@/features/activo-fijo/Transferencias';
import { CategoriasAF } from '@/features/activo-fijo/Categorias';
import { UbicacionesAF } from '@/features/activo-fijo/Ubicaciones';
import { CustodiosAF } from '@/features/activo-fijo/Custodios';
import { ParametrosAF } from '@/features/activo-fijo/ParametrosAF';
import { EnConstruccionCB } from '@/features/control-bancario/shared';
import { AyudaNomina } from '@/features/nomina/AyudaNomina';
import { SmtpNomina } from '@/features/nomina/SmtpNomina';
import { Turnos } from '@/features/nomina/Turnos';
import { Asistencia } from '@/features/nomina/Asistencia';
import { Licencias } from '@/features/nomina/Licencias';
import { Feriados } from '@/features/nomina/Feriados';
import { CambiarClaveGate } from '@/components/auth/CambiarClaveGate';
import { PuntoVenta } from '@/features/pos/PuntoVenta';
import { ConfigDeuna } from '@/features/pos/ConfigDeuna';
import { ConfigPasarela } from '@/features/pos/ConfigPasarela';
import { ConfigImpresion } from '@/features/pos/ConfigImpresion';
import { ConciliacionCierres } from '@/features/pos/ConciliacionCierres';
import { Recargas } from '@/features/pos/Recargas';
import { ConexionBroadnet } from '@/features/pos/ConexionBroadnet';
import { ProductosBroadnet } from '@/features/pos/ProductosBroadnet';
import { TerminalesBroadnet } from '@/features/pos/TerminalesBroadnet';
import { CodigosBroadnet } from '@/features/pos/CodigosBroadnet';
import { MonitoreoBroadnet } from '@/features/pos/MonitoreoBroadnet';
import { AyudaBroadnet } from '@/features/pos/AyudaBroadnet';
import { DashboardPos } from '@/features/pos/DashboardPos';
import { AyudaPos } from '@/features/pos/AyudaPos';
import { Devoluciones } from '@/features/pos/Devoluciones';
import { AperturaCaja, MovimientosCaja, CierreArqueo } from '@/features/pos/Caja';
import { RepVentas, RepCierres, RepFormasPago, RepTopProductos, RepVentasBodega } from '@/features/pos/Reportes';
import { DocumentosPos } from '@/features/pos/DocumentosPos';
import { ConciliacionTarjetas } from '@/features/pos/ConciliacionTarjetas';
import { AuditoriaVentas } from '@/features/pos/AuditoriaVentas';
import { VentasDia } from '@/features/pos/VentasDia';
import { Cajeros } from '@/features/pos/Cajeros';
import { Promociones } from '@/features/pos/Promociones';
import { Fidelidad } from '@/features/pos/Fidelidad';
import { Niveles } from '@/features/pos/Niveles';
import { Sellos } from '@/features/pos/Sellos';
import { DashboardGanaderia } from '@/features/ganaderia/DashboardGanaderia';
import { Hato } from '@/features/ganaderia/Hato';
import { Movimientos as MovimientosGanaderia } from '@/features/ganaderia/Movimientos';
import { PrediosPotreros } from '@/features/ganaderia/PrediosPotreros';
import { Lotes } from '@/features/ganaderia/Lotes';
import { Sanidad as SanidadGanaderia } from '@/features/ganaderia/Sanidad';
import { Reproduccion as ReproduccionGanaderia } from '@/features/ganaderia/Reproduccion';
import { Leche as LecheGanaderia } from '@/features/ganaderia/Leche';
import { Costos as CostosGanaderia } from '@/features/ganaderia/Costos';
import { AyudaGanaderia } from '@/features/ganaderia/AyudaGanaderia';
import { Pacientes } from '@/features/medical/Pacientes';
import { PacientesDuplicados } from '@/features/medical/PacientesDuplicados';
import { Medicos } from '@/features/medical/Medicos';
import { Especialidades } from '@/features/medical/Especialidades';
import { Agenda } from '@/features/medical/Agenda';
import { ListaEspera } from '@/features/medical/ListaEspera';
import { HistoriaClinica } from '@/features/medical/HistoriaClinica';
import { Plantillas } from '@/features/medical/Plantillas';
import { Convenios } from '@/features/medical/Convenios';
import { Aptitud } from '@/features/medical/Aptitud';
import { ReporteDerivaciones } from '@/features/medical/ReporteDerivaciones';
import { Recetas } from '@/features/medical/Recetas';
import { Misiones } from '@/features/medical/Misiones';
import { PresidentesMision } from '@/features/medical/PresidentesMision';
import { Certificados } from '@/features/medical/Certificados';
import { MiClinica } from '@/features/medical/MiClinica';
import { Examenes } from '@/features/medical/Examenes';
import { Laboratorio } from '@/features/medical/Laboratorio';
import { Teleconsulta } from '@/features/medical/Teleconsulta';
import { Mensajes } from '@/features/medical/Mensajes';
import { SolicitudesReceta } from '@/features/medical/SolicitudesReceta';
import { Recursos } from '@/features/medical/Recursos';
import { CalendarioCorp } from '@/features/medical/CalendarioCorp';
import { DesignShowcase } from '@/features/medical/design/Showcase';
import { Dashboard as DashboardMedico } from '@/features/medical/Dashboard';
import { AyudaMedical } from '@/features/medical/AyudaMedical';
import { AyudaTelemedicina } from '@/features/medical/AyudaTelemedicina';
import { AyudaReclamosAseguradora } from '@/features/medical/AyudaReclamosAseguradora';
import { ConsultaReclamos } from '@/features/medical/ConsultaReclamos';
import { PortalPaciente } from '@/features/medical/PortalPaciente';
import { Consentimientos } from '@/features/medical/Consentimientos';
import { Canales as CanalesMedicos } from '@/features/medical/Canales';
import { Visitantes } from '@/features/medical/Visitantes';
import { VirtualAssistant } from '@/components/assistant/VirtualAssistant';
import { VoiceOrb } from '@/components/voice/VoiceOrb';
import { VoiceFormProvider } from '@/contexts/VoiceFormContext';
import { PwaUpdater } from '@/components/pwa/PwaUpdater';
import '@/lib/i18n'; // Inicializar i18n

// Refresco de datos sin recargar la página (pestañas keep-alive):
//  (1) Tras CUALQUIER mutación exitosa (crear/editar/eliminar) se marcan TODAS las
//      consultas como "vencidas" SIN refetch inmediato (refetchType:'none') → no hay
//      ráfaga de peticiones; solo se marca que hay que refrescar.
//  (2) Al activar/volver a una pestaña (ver AppContent) se refrescan las consultas
//      montadas que estén vencidas → la pantalla a la que regresas se actualiza sola.
// Así, un valor creado en una pantalla aparece en la otra sin Ctrl+R.
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: () => { void queryClient.invalidateQueries({ refetchType: 'none' }); },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minuto (los catálogos estáticos fijan staleTime: Infinity)
      retry: 1,
      refetchOnWindowFocus: true, // al volver al navegador refresca lo vencido
    },
  },
});

function SinModulos() {
  return (
    <div className="flex h-full items-center justify-center p-8 text-center">
      <div className="max-w-md space-y-2">
        <h2 className="text-lg font-semibold">No tienes módulos asignados</h2>
        <p className="text-sm text-muted-foreground">Tu perfil aún no tiene opciones de menú habilitadas. Contacta al administrador para que te asigne permisos.</p>
      </div>
    </div>
  );
}

function AppContent() {
  const { activeTabId, tabs, openTab } = useTabsStore();
  const { data: menusData, isLoading: menusLoading } = useMenus();

  // Al volver/activar una pestaña, refresca en segundo plano las consultas
  // montadas que quedaron vencidas (p. ej. tras crear un dato en otra pantalla).
  // No bloquea la UI: muestra la caché y se actualiza al llegar la respuesta.
  useEffect(() => {
    if (activeTabId) void queryClient.refetchQueries({ type: 'active', stale: true });
  }, [activeTabId]);

  // MODO APP (733): perfiles marcados ven la interfaz mínima (sin menú/encabezado)
  const modoApp = menusData?.perfil?.modo_app === true;
  const opcionesApp = useMemo<OpcionApp[]>(() => {
    const out: OpcionApp[] = [];
    for (const m of menusData?.menus ?? []) {
      for (const sub of m.submenus ?? []) {
        for (const o of sub.opciones ?? []) {
          if (o.ruta) out.push({ ruta: o.ruta, nombre: o.nombre, icon: o.icon ?? null });
        }
      }
    }
    return out;
  }, [menusData]);

  // Landing: primera opción navegable del menú del perfil (en el orden del sidebar).
  const primeraOpcion = useMemo(() => {
    for (const m of menusData?.menus ?? []) {
      for (const s of m.submenus ?? []) {
        for (const o of s.opciones ?? []) {
          if (o.ruta) return o;
        }
      }
    }
    return null;
  }, [menusData]);

  // Al cargar los menús, si no hay pestañas abiertas, abrir la primera opción del perfil.
  useEffect(() => {
    if (tabs.length === 0 && primeraOpcion) {
      openTab({ id: primeraOpcion.ruta, title: primeraOpcion.nombre, path: primeraOpcion.ruta, icon: primeraOpcion.icon || undefined });
    }
  }, [tabs.length, primeraOpcion, openTab]);

  // Renderizar contenido según la tab activa
  const renderContentForTab = (tab: Tab) => {
    const activeTab = tab;

    // Financiero · Gestión SRI (las rutas vienen de autenticacion.opciones)
    if (activeTab?.path === '/sri/documentos-recibidos') {
      return <DocumentosRecibidosSri />;
    }
    if (activeTab?.path === '/sri/dashboard') {
      return <DashboardSri />;
    }
    if (activeTab?.path === '/sri/procesar-recibidos') {
      return <ProcesarRecibidos />;
    }
    if (activeTab?.path === '/sri/documentos-emitidos') {
      return <DocumentosEmitidosSri />;
    }
    if (activeTab?.path === '/sri/procesar-emitidos') {
      return <ProcesarEmitidos />;
    }
    if (activeTab?.path === '/financiero/dashboard-ventas') {
      return <DashboardFinanciero />;
    }
    if (activeTab?.path === '/financiero/dashboard-compras') {
      return <DashboardFinanciero inicial="compras" />;
    }
    if (activeTab?.path === '/financiero/bandeja-emitidos') {
      return <BandejaEmitidos />;
    }
    if (activeTab?.path === '/financiero/emitidos-conciliacion') {
      return <ConciliacionEmitidos />;
    }
    if (activeTab?.path === '/financiero/retenciones-reporte') {
      return <ReporteRetenciones />;
    }
    if (activeTab?.path === '/financiero/aging') {
      return <ReporteAging />;
    }
    if (activeTab?.path === '/financiero/estado-cuenta') {
      return <ReporteEstadoCuenta />;
    }
    if (activeTab?.path === '/financiero/ayuda') {
      return <AyudaFinanciero />;
    }
    if (activeTab?.path === '/pos/tv') {
      return <TvDispositivos />;
    }
    if (activeTab?.path === '/pos/tv-plantillas') {
      return <TvPlantillas />;
    }
    if (activeTab?.path === '/pos/tv-medios') {
      return <TvMedios />;
    }
    if (activeTab?.path === '/cxc/facturas') {
      return <CxcFacturas />;
    }
    if (activeTab?.path === '/cxp/facturas') {
      return <CxpFacturas />;
    }
    if (activeTab?.path === '/cxp/registrar-compra') {
      return <RegistrarCompra />;
    }
    if (activeTab?.path === '/cxp/prefacturas') {
      return <PrefacturasProveedor />;
    }
    if (activeTab?.path === '/cxp/liquidacion-compra') {
      return <LiquidacionCompra />;
    }
    if (activeTab?.path === '/cxc/notas-credito') {
      return <CxcNotasCredito />;
    }
    if (activeTab?.path === '/cxp/notas-credito') {
      return <CxpNotasCredito />;
    }
    if (activeTab?.path === '/cxc/anticipos') {
      return <CxcAnticipos />;
    }
    if (activeTab?.path === '/cxp/anticipos') {
      return <CxpAnticipos />;
    }
    if (activeTab?.path === '/cxc/retenciones') {
      return <CxcRetenciones />;
    }
    if (activeTab?.path === '/cxp/retenciones') {
      return <CxpRetenciones />;
    }
    if (activeTab?.path === '/cxc/facturacion-especializada') {
      return <FacturacionEspecializada />;
    }
    if (activeTab?.path === '/cxc/facturacion') {
      return <Facturacion />;
    }
    if (activeTab?.path === '/cxc/notas-credito-emitir') {
      return <NotasCredito />;
    }
    if (activeTab?.path === '/cxc/notas-debito-emitir') {
      return <NotasDebito />;
    }
    if (activeTab?.path === '/cxc/cotizaciones') {
      return <Cotizaciones />;
    }
    if (activeTab?.path === '/cxp/retenciones-emitir') {
      return <Retenciones />;
    }
    if (activeTab?.path === '/contabilidad/conciliacion-tributaria') {
      return <Tributario tabInicial="101" />;
    }
    if (activeTab?.path === '/contabilidad/correo') {
      return <CorreoContabilidad />;
    }
    if (activeTab?.path === '/contabilidad/plan-cuentas') {
      return <PlanCuentas />;
    }
    if (activeTab?.path === '/contabilidad/config') {
      return <ConfigContable />;
    }
    if (activeTab?.path === '/contabilidad/asientos-recurrentes') {
      return <AsientosRecurrentes />;
    }
    if (activeTab?.path === '/contabilidad/asientos') {
      return <Asientos />;
    }
    if (activeTab?.path === '/contabilidad/libro-mayor') {
      return <LibroMayor />;
    }
    if (activeTab?.path === '/contabilidad/mayor-terceros') {
      return <MayorTerceros />;
    }
    if (activeTab?.path === '/contabilidad/comprobacion') {
      return <Comprobacion />;
    }
    if (activeTab?.path === '/contabilidad/balance') {
      return <EstadoSituacion />;
    }
    if (activeTab?.path === '/contabilidad/resultados') {
      return <EstadoResultados />;
    }
    if (activeTab?.path === '/contabilidad/resultados-niif18') {
      return <EstadoResultadosNIIF18 />;
    }
    if (activeTab?.path === '/contabilidad/gastos-naturaleza') {
      return <GastosPorNaturaleza />;
    }
    if (activeTab?.path === '/contabilidad/mpm') {
      return <MPM />;
    }
    if (activeTab?.path === '/contabilidad/ayuda-niif18') {
      return <AyudaNIIF18 />;
    }
    if (activeTab?.path === '/contabilidad/centros-costo') {
      return <CentrosCosto />;
    }
    if (activeTab?.path === '/contabilidad/proyectos') {
      return <Proyectos />;
    }
    if (activeTab?.path === '/contabilidad/analitico') {
      return <ResultadoAnalitico />;
    }
    if (activeTab?.path === '/contabilidad/bi') {
      return <BI />;
    }
    if (activeTab?.path === '/contabilidad/presupuesto') {
      return <Presupuesto />;
    }
    if (activeTab?.path === '/contabilidad/bi-envios') {
      return <BIEnvios />;
    }
    if (activeTab?.path === '/contabilidad/periodos') {
      return <PeriodosCierre />;
    }
    if (activeTab?.path === '/contabilidad/libro-diario') {
      return <LibroDiario />;
    }
    if (activeTab?.path === '/contabilidad/flujo-efectivo') {
      return <FlujoEfectivoContab />;
    }
    if (activeTab?.path === '/contabilidad/patrimonio') {
      return <CambiosPatrimonio />;
    }
    if (activeTab?.path === '/contabilidad/tributario') {
      return <Tributario />;
    }
    if (activeTab?.path === '/contabilidad/ats') {
      return <ATS />;
    }
    if (activeTab?.path === '/contabilidad/dashboard') {
      return <DashboardContabilidad />;
    }
    if (activeTab?.path === '/contabilidad/ayuda') {
      return <AyudaContabilidad />;
    }
    if (activeTab?.path === '/contabilidad/prorrateo') {
      return <Prorrateo />;
    }
    if (activeTab?.path === '/financiero/emision-config') {
      return <ConfiguracionEmision />;
    }
    if (activeTab?.path === '/financiero/comunicacion-baja') {
      return <ComunicacionBaja />;
    }
    if (activeTab?.path === '/financiero/ayuda-sunat') {
      return <AyudaSunatPe />;
    }
    if (activeTab?.path === '/mision/mis-pacientes') {
      return <MisPacientes />;
    }
    if (activeTab?.path === '/mision/dashboard-atenciones') {
      return <MisionDashboard />;
    }
    if (activeTab?.path === '/mision/agendar-atencion') {
      return <MisionAgendar />;
    }
    if (activeTab?.path === '/mision/enviar-mensaje') {
      return <MisionMensaje />;
    }
    if (activeTab?.path === '/mision/reporte-atenciones') {
      return <MisionReporte />;
    }
    if (activeTab?.path === '/mision/reporte-facturacion') {
      return <MisionReporteFacturacion />;
    }
    if (activeTab?.path === '/trading/dashboard') {
      return <TradingDashboard />;
    }
    if (activeTab?.path === '/trading/transacciones') {
      return <TradingTransacciones />;
    }
    if (activeTab?.path === '/trading/binance') {
      return <TradingBinance />;
    }
    if (activeTab?.path === '/trading/ayuda') {
      return <AyudaTrading />;
    }
    if (activeTab?.path === '/inventario/dashboard') {
      return <DashboardInventario />;
    }
    if (activeTab?.path === '/inventario/ayuda') {
      return <AyudaInventario />;
    }
    if (activeTab?.path === '/inventario/categorias') {
      return <Categorias />;
    }
    if (activeTab?.path === '/inventario/productos') {
      return <Productos />;
    }
    if (activeTab?.path === '/inventario/atributos') {
      return <Atributos />;
    }
    if (activeTab?.path === '/inventario/ubicaciones') {
      return <Ubicaciones />;
    }
    if (activeTab?.path === '/inventario/precios-compra') {
      return <PreciosCompra />;
    }
    if (activeTab?.path === '/inventario/reposicion') {
      return <Reposicion />;
    }
    if (activeTab?.path === '/inventario/abc-productos') {
      return <AbcProductos />;
    }
    if (activeTab?.path === '/inventario/liquidacion') {
      return <MarkdownLiquidacion />;
    }
    if (activeTab?.path === '/inventario/rentabilidad-config') {
      return <RentabilidadConfig />;
    }
    if (activeTab?.path === '/inventario/reubicacion') {
      return <Reubicaciones />;
    }
    if (activeTab?.path === '/inventario/conteos') {
      return <TomaFisica />;
    }
    if (activeTab?.path === '/inventario/reservas') {
      return <Reservas />;
    }
    if (activeTab?.path === '/inventario/cierre-periodo') {
      return <CierrePeriodo />;
    }
    if (activeTab?.path === '/inventario/costos') {
      return <CostosInventario />;
    }
    if (activeTab?.path === '/inventario/stock-ubicacion') {
      return <StockUbicaciones />;
    }
    if (activeTab?.path === '/produccion/ordenes') {
      return <OrdenesTrabajo />;
    }
    if (activeTab?.path === '/produccion/recetas') {
      return <RecetasProduccion />;
    }
    // Hotel (PMS) — scripts 915-922
    {
      const hotel: Record<string, () => JSX.Element> = {
        '/hotel/dashboard': () => <DashboardHotel />,
        '/hotel/rack': () => <RackHotel />,
        '/hotel/reservas': () => <ReservasHotel />,
        '/hotel/housekeeping': () => <HousekeepingHotel />,
        '/hotel/mi-dia': () => <MiDiaHotel />,
        '/hotel/mantenimiento': () => <MantenimientoHotel />,
        '/hotel/cierre': () => <CierreNocturnoHotel />,
        '/hotel/tarifas': () => <TarifasHotel />,
        '/hotel/catalogo': () => <CatalogoHotel />,
        '/hotel/config': () => <ConfigHotel />,
        '/hotel/feriados': () => <FeriadosHotel />,
        '/hotel/parte': () => <ParteHuespedes />,
        '/hotel/grupos': () => <GruposHotel />,
        '/hotel/reportes': () => <ReportesHotel />,
        '/hotel/caja': () => <CajaRecepcion />,
        '/hotel/pasarela': () => <ConfigPasarela moduloInicial="hotel" soloModulo />,
        '/hotel/ayuda': () => <AyudaHotel />,
      };
      const vista = activeTab?.path ? hotel[activeTab.path] : undefined;
      if (vista) return vista();
    }
    // Taller-Game — script 953
    {
      const taller: Record<string, () => JSX.Element> = {
        '/taller/tablero': () => <TableroTaller />,
        '/taller/ordenes': () => <OrdenesTaller />,
        '/taller/recepcion': () => <RecepcionTaller />,
        '/taller/mi-banco': () => <MiBanco />,
        '/taller/informes': () => <InformesTaller />,
        '/taller/retiros': () => <RetirosTaller />,
        '/taller/planes': () => <PlanesTaller />,
        '/taller/usados': () => <UsadosTaller />,
        '/taller/comisiones': () => <ComisionesTaller />,
        '/taller/caja-cobros': () => <CajaCobrosTaller />,
        '/taller/caja': () => <CajaTaller />,
        '/taller/facturas': () => <FacturasTaller />,
        '/taller/perfiles': () => <PerfilesTaller />,
        '/taller/pasarela': () => <ConfigPasarela moduloInicial="taller" soloModulo />,
        '/taller/catalogo': () => <CatalogoTaller />,
        '/taller/config': () => <ConfigTaller />,
        '/taller/ayuda': () => <AyudaTaller />,
      };
      const vista = activeTab?.path ? taller[activeTab.path] : undefined;
      if (vista) return vista();
    }
    if (activeTab?.path === '/catering/dashboard') {
      return <DashboardCatering />;
    }
    if (activeTab?.path === '/catering/ayuda') {
      return <AyudaCatering />;
    }
    if (activeTab?.path === '/catering/piso') {
      return <PisoCatering />;
    }
    if (activeTab?.path === '/catering/andon') {
      return <AndonCatering />;
    }
    if (activeTab?.path === '/catering/estaciones') {
      return <EstacionesCatering />;
    }
    if (activeTab?.path === '/catering/capacidad') {
      return <CapacidadCatering />;
    }
    if (activeTab?.path === '/catering/calibracion') {
      return <CalibracionCatering />;
    }
    if (activeTab?.path === '/catering/calendario') {
      return <CalendarioCatering />;
    }
    if (activeTab?.path === '/catering/abastecimiento') {
      return <AbastecimientoCatering />;
    }
    if (activeTab?.path === '/catering/comensales') {
      return <ComensalesCatering />;
    }
    if (activeTab?.path === '/catering/opciones') {
      return <OpcionesCatering />;
    }
    if (activeTab?.path === '/catering/consumo') {
      return <ConsumoCatering />;
    }
    if (activeTab?.path === '/catering/prescripciones') {
      return <PrescripcionesCatering />;
    }
    if (activeTab?.path === '/catering/cadena-frio') {
      return <CadenaFrioCatering />;
    }
    if (activeTab?.path === '/catering/flota') {
      return <FlotaCatering />;
    }
    if (activeTab?.path === '/catering/esg') {
      return <EsgCatering />;
    }
    if (activeTab?.path === '/catering/recetas') {
      return <RecetasCatering />;
    }
    if (activeTab?.path === '/catering/menus') {
      return <MenusCatering />;
    }
    if (activeTab?.path === '/catering/produccion') {
      return <ProduccionCatering />;
    }
    if (activeTab?.path === '/catering/contratos') {
      return <ContratosCatering />;
    }
    if (activeTab?.path === '/catering/cortes') {
      return <CortesCatering />;
    }
    if (activeTab?.path === '/catering/costeo') {
      return <CosteoCatering />;
    }
    if (activeTab?.path === '/catering/rutas') {
      return <RutasCatering />;
    }
    if (activeTab?.path === '/catering/despacho') {
      return <DespachoCatering />;
    }
    if (activeTab?.path === '/catering/menaje') {
      return <MenajeCatering />;
    }
    if (activeTab?.path === '/catering/nutricion') {
      return <NutricionCatering />;
    }
    if (activeTab?.path === '/catering/dietas') {
      return <DietasCatering />;
    }
    if (activeTab?.path === '/catering/pronostico') {
      return <PronosticoCatering />;
    }
    if (activeTab?.path === '/catering/haccp') {
      return <HaccpCatering />;
    }
    if (activeTab?.path === '/catering/licitacion') {
      return <LicitacionCatering />;
    }
    if (activeTab?.path === '/catering/optimizador') {
      return <OptimizadorCatering />;
    }
    if (activeTab?.path === '/catering/ingenieria') {
      return <IngenieriaCatering />;
    }
    if (activeTab?.path === '/inventario/marcas') {
      return <Marcas />;
    }
    if (activeTab?.path === '/inventario/motivos-ajuste') {
      return <MotivosAjuste />;
    }
    if (activeTab?.path === '/inventario/listas-precios') {
      return <ListasPrecios />;
    }
    if (activeTab?.path === '/inventario/precios') {
      return <Precios />;
    }
    if (activeTab?.path === '/inventario/bodegas') {
      return <Bodegas />;
    }
    if (activeTab?.path === '/inventario/permisos-bodega') {
      return <PermisosBodega />;
    }
    if (activeTab?.path === '/inventario/clasificacion-ventas') {
      return <ClasificacionVentas />;
    }
    if (activeTab?.path === '/inventario/codigos-producto') {
      return <CodigosProducto />;
    }
    // POS
    if (activeTab?.path === '/pos/venta') {
      return <PuntoVenta />;
    }
    if (activeTab?.path === '/pos/pasarela') {
      return <ConfigPasarela />;
    }
    if (activeTab?.path === '/pos/deuna') {
      return <ConfigDeuna />;
    }
    if (activeTab?.path === '/pos/formato-impresion') {
      return <ConfigImpresion />;
    }
    if (activeTab?.path === '/pos/conciliacion') {
      return <ConciliacionCierres />;
    }
    if (activeTab?.path === '/pos/recargas') {
      return <Recargas />;
    }
    if (activeTab?.path === '/pos/broadnet/conexion') {
      return <ConexionBroadnet />;
    }
    if (activeTab?.path === '/pos/broadnet/productos') {
      return <ProductosBroadnet />;
    }
    if (activeTab?.path === '/pos/broadnet/terminales') {
      return <TerminalesBroadnet />;
    }
    if (activeTab?.path === '/pos/broadnet/codigos') {
      return <CodigosBroadnet />;
    }
    if (activeTab?.path === '/pos/broadnet/monitoreo') {
      return <MonitoreoBroadnet />;
    }
    if (activeTab?.path === '/pos/broadnet/ayuda') {
      return <AyudaBroadnet />;
    }
    if (activeTab?.path === '/pos/dashboard') {
      return <DashboardPos />;
    }
    if (activeTab?.path === '/pos/ayuda') {
      return <AyudaPos />;
    }
    if (activeTab?.path === '/pos/devoluciones') {
      return <Devoluciones />;
    }
    if (activeTab?.path === '/pos/apertura') {
      return <AperturaCaja />;
    }
    if (activeTab?.path === '/pos/movimientos') {
      return <MovimientosCaja />;
    }
    if (activeTab?.path === '/pos/cierre') {
      return <CierreArqueo />;
    }
    if (activeTab?.path === '/pos/rep-ventas') {
      return <RepVentas />;
    }
    if (activeTab?.path === '/pos/rep-documentos') {
      return <DocumentosPos />;
    }
    if (activeTab?.path === '/pos/conciliacion-tarjetas') {
      return <ConciliacionTarjetas />;
    }
    if (activeTab?.path === '/pos/auditoria') {
      return <AuditoriaVentas />;
    }
    if (activeTab?.path === '/sistemas/arquitectura') {
      return <ArquitecturaSistema />;
    }
    if (activeTab?.path === '/sistemas/ayuda') {
      return <AyudaSistemas />;
    }
    if (activeTab?.path === '/sistemas/asistente') {
      return <AsistenteConfig />;
    }
    if (activeTab?.path === '/seguridad/respaldos') {
      return <Respaldos />;
    }
    if (activeTab?.path === '/pos/rest-dashboard') {
      return <RestDashboard />;
    }
    if (activeTab?.path === '/pos/rest-movil') {
      return <MeseroMovil />;
    }
    if (activeTab?.path === '/pos/rest-salon') {
      return <Salon />;
    }
    if (activeTab?.path === '/pos/rest-mesas') {
      return <MesasRestaurant />;
    }
    if (activeTab?.path === '/pos/rest-kds') {
      return <KdsRestaurant />;
    }
    if (activeTab?.path === '/pos/rest-estaciones') {
      return <EstacionesRestaurant />;
    }
    if (activeTab?.path === '/pos/rest-meseros') {
      return <MeserosRestaurant />;
    }
    if (activeTab?.path === '/pos/rest-config') {
      return <ConfigRestaurant />;
    }
    if (activeTab?.path === '/pos/rest-reportes') {
      return <ReportesRestaurant />;
    }
    if (activeTab?.path === '/pos/rest-reservas') {
      return <ReservasRestaurant />;
    }
    if (activeTab?.path === '/pos/rest-recetas') {
      return <RecetasRestaurant />;
    }
    if (activeTab?.path === '/pos/rest-fotos') {
      return <FotosMenu />;
    }
    if (activeTab?.path === '/signage/preview') {
      return <SignagePreview />;
    }
    if (activeTab?.path === '/signage/dashboard') {
      return <DashboardSignage />;
    }
    if (activeTab?.path === '/signage/pantallas') {
      return <SignagePantallas />;
    }
    if (activeTab?.path === '/signage/plantillas') {
      return <SignagePlantillas />;
    }
    if (activeTab?.path === '/signage/programacion') {
      return <SignageProgramacion />;
    }
    if (activeTab?.path === '/signage/grupos') {
      return <SignageGrupos />;
    }
    if (activeTab?.path === '/signage/temas') {
      return <SignageTemas />;
    }
    if (activeTab?.path === '/signage/campanas') {
      return <SignageCampanas />;
    }
    if (activeTab?.path === '/signage/ayuda') {
      return <AyudaSignage />;
    }
    if (activeTab?.path === '/signage/alertas') {
      return <AlertasSignage />;
    }
    if (activeTab?.path === '/signage/uplift') {
      return <SignageUplift />;
    }
    if (activeTab?.path === '/signage/reglas') {
      return <SignageReglas />;
    }
    if (activeTab?.path === '/signage/anuncios') {
      return <SignageAnuncios />;
    }
    if (activeTab?.path === '/signage/phygital') {
      return <SignagePhygital />;
    }
    if (activeTab?.path === '/signage/medios') {
      return <SignageMedios />;
    }
    if (activeTab?.path === '/signage/experimentos') {
      return <SignageExperimentos />;
    }
    if (activeTab?.path === '/signage/feeds') {
      return <SignageFeeds />;
    }
    if (activeTab?.path === '/signage/anunciantes') {
      return <SignageAnunciantes />;
    }
    if (activeTab?.path === '/signage/contratos') {
      return <SignageContratos />;
    }
    if (activeTab?.path === '/signage/combos') {
      return <SignageCombos />;
    }
    if (activeTab?.path === '/signage/cupones') {
      return <SignageCupones />;
    }
    if (activeTab?.path === '/signage/tarifario') {
      return <SignageTarifario />;
    }
    if (activeTab?.path === '/signage/avisos') {
      return <SignageAvisos />;
    }
    if (activeTab?.path === '/signage/salud') {
      return <SignageSalud />;
    }
    if (activeTab?.path === '/signage/social') {
      return <SignageSocial />;
    }
    if (activeTab?.path === '/signage/buzon') {
      return <SignageBuzon />;
    }
    if (activeTab?.path === '/pos/rest-pedidos-ext') {
      return <PedidosExternosRestaurant />;
    }
    if (activeTab?.path === '/pos/rest-ayuda') {
      return <AyudaRestaurant />;
    }
    if (activeTab?.path === '/ecommerce/dashboard') {
      return <DashboardWeb />;
    }
    if (activeTab?.path === '/ecommerce/publicacion') {
      return <PublicacionWeb />;
    }
    if (activeTab?.path === '/ecommerce/colecciones') {
      return <ColeccionesWeb />;
    }
    if (activeTab?.path === '/ecommerce/banners') {
      return <BannersWeb />;
    }
    if (activeTab?.path === '/ecommerce/paginas') {
      return <PaginasWeb />;
    }
    if (activeTab?.path === '/ecommerce/pedidos') {
      return <PedidosWeb />;
    }
    if (activeTab?.path === '/ecommerce/carritos') {
      return <CarritosWeb />;
    }
    if (activeTab?.path === '/ecommerce/config') {
      return <ConfigTienda />;
    }
    if (activeTab?.path === '/ecommerce/pagos') {
      return <PagosWeb />;
    }
    if (activeTab?.path === '/ecommerce/envios') {
      return <EnviosWeb />;
    }
    if (activeTab?.path === '/crm/prospectos') {
      return <Prospectos />;
    }
    if (activeTab?.path === '/crm/contratos') {
      return <ContratosCrm />;
    }
    if (activeTab?.path === '/crm/whatsapp') {
      return <BandejaWhatsApp />;
    }
    if (activeTab?.path === '/medical/misiones') {
      return <Misiones />;
    }
    if (activeTab?.path === '/medical/presidentes') {
      return <PresidentesMision />;
    }
    if (activeTab?.path === '/pos/ventas-dia') {
      return <VentasDia />;
    }
    if (activeTab?.path === '/pos/cierre-z') {
      return <RepCierres />;
    }
    if (activeTab?.path === '/pos/rep-formas-pago') {
      return <RepFormasPago />;
    }
    if (activeTab?.path === '/pos/rep-top-productos') {
      return <RepTopProductos />;
    }
    if (activeTab?.path === '/pos/rep-ventas-bodega') {
      return <RepVentasBodega />;
    }
    // POS: catálogos con CRUD; el resto de /pos/* son placeholders (fase futura)
    if (activeTab?.path === '/pos/cajas') {
      return <Cajas />;
    }
    if (activeTab?.path === '/pos/balanza-etiqueta') {
      return <ConfiguracionBalanza />;
    }
    if (activeTab?.path === '/pos/formas-pago') {
      return <FormasPago />;
    }
    if (activeTab?.path === '/pos/denominaciones') {
      return <Denominaciones />;
    }
    if (activeTab?.path === '/pos/cajeros') {
      return <Cajeros />;
    }
    if (activeTab?.path === '/pos/promociones') {
      return <Promociones />;
    }
    if (activeTab?.path === '/pos/fidelidad') {
      return <Fidelidad />;
    }
    if (activeTab?.path === '/pos/niveles') {
      return <Niveles />;
    }
    if (activeTab?.path === '/pos/sellos') {
      return <Sellos />;
    }
    if (activeTab?.path?.startsWith('/pos/')) {
      return <EnConstruccion titulo={activeTab.title} />;
    }
    if (activeTab?.path === '/control-bancario/dashboard') {
      return <DashboardControlBancario />;
    }
    if (activeTab?.path === '/control-bancario/ayuda') {
      return <AyudaControlBancario />;
    }
    if (activeTab?.path === '/control-bancario/bancos') {
      return <BancosCB />;
    }
    if (activeTab?.path === '/control-bancario/cuentas') {
      return <CuentasCB />;
    }
    if (activeTab?.path === '/control-bancario/posicion') {
      return <PosicionCB />;
    }
    if (activeTab?.path === '/control-bancario/config-parametros') {
      return <ParametrosCB />;
    }
    if (activeTab?.path === '/control-bancario/config-conciliacion') {
      return <ReglasConciliacion />;
    }
    if (activeTab?.path === '/control-bancario/config-formatos') {
      return <FormatosExtracto />;
    }
    if (activeTab?.path === '/control-bancario/config-contable') {
      return <MapeoContableCB />;
    }
    if (activeTab?.path === '/control-bancario/config-correo') {
      return <SmtpControlBancario />;
    }
    if (activeTab?.path === '/activo-fijo/dashboard') {
      return <DashboardAF />;
    }
    if (activeTab?.path === '/activo-fijo/ayuda') {
      return <AyudaActivoFijo />;
    }
    if (activeTab?.path === '/activo-fijo/activos') {
      return <ActivosAF />;
    }
    if (activeTab?.path === '/activo-fijo/categorias') {
      return <CategoriasAF />;
    }
    if (activeTab?.path === '/activo-fijo/ubicaciones') {
      return <UbicacionesAF />;
    }
    if (activeTab?.path === '/activo-fijo/custodios') {
      return <CustodiosAF />;
    }
    if (activeTab?.path === '/activo-fijo/config-parametros') {
      return <ParametrosAF />;
    }
    if (activeTab?.path === '/activo-fijo/depreciacion') {
      return <DepreciacionAF />;
    }
    if (activeTab?.path === '/activo-fijo/depreciacion-periodos') {
      return <PeriodosAF />;
    }
    if (activeTab?.path === '/activo-fijo/config-cuentas') {
      return <MapeoAF />;
    }
    if (activeTab?.path === '/activo-fijo/bajas') {
      return <BajasAF />;
    }
    if (activeTab?.path === '/activo-fijo/ventas') {
      return <VentasAF />;
    }
    if (activeTab?.path === '/activo-fijo/rep-registro') {
      return <RegistroAF />;
    }
    if (activeTab?.path === '/activo-fijo/rep-movimiento') {
      return <MovimientoAF />;
    }
    if (activeTab?.path === '/activo-fijo/rep-proyeccion') {
      return <ProyeccionAF />;
    }
    if (activeTab?.path === '/activo-fijo/rep-conciliacion') {
      return <ConciliacionAF />;
    }
    if (activeTab?.path === '/activo-fijo/rep-depreciados') {
      return <DepreciadosAF />;
    }
    if (activeTab?.path === '/activo-fijo/rep-distribucion') {
      return <DistribucionAF />;
    }
    if (activeTab?.path === '/activo-fijo/rep-fiscal') {
      return <FiscalAF />;
    }
    if (activeTab?.path === '/activo-fijo/etiquetas') {
      return <EtiquetasAF />;
    }
    if (activeTab?.path === '/activo-fijo/toma-fisica') {
      return <TomaFisicaAF />;
    }
    if (activeTab?.path === '/activo-fijo/toma-fisica-conciliacion') {
      return <ConciliacionTomaAF />;
    }
    if (activeTab?.path === '/activo-fijo/revalorizacion') {
      return <RevalorizacionAF />;
    }
    if (activeTab?.path === '/activo-fijo/revision-vida-util') {
      return <RevisionVidaUtilAF />;
    }
    if (activeTab?.path === '/activo-fijo/arrendamientos') {
      return <ArrendamientosAF />;
    }
    if (activeTab?.path === '/activo-fijo/capitalizar') {
      return <CapitalizarAF />;
    }
    if (activeTab?.path === '/activo-fijo/impuesto-diferido') {
      return <ImpuestoDiferidoAF />;
    }
    if (activeTab?.path === '/activo-fijo/deterioro') {
      return <DeterioroAF />;
    }
    if (activeTab?.path === '/activo-fijo/config-placas') {
      return <PlacasAF />;
    }
    if (activeTab?.path === '/activo-fijo/mejoras') {
      return <MejorasAF />;
    }
    if (activeTab?.path === '/activo-fijo/componentes') {
      return <ComponentesAF />;
    }
    if (activeTab?.path === '/activo-fijo/carga-inicial') {
      return <CargaInicialAF />;
    }
    if (activeTab?.path === '/activo-fijo/transferencias') {
      return <TransferenciasAF />;
    }
    if (activeTab?.path === '/activo-fijo/depreciacion-fiscal') {
      return <FiscalAF />;
    }
    if (activeTab?.path === '/control-bancario/movimientos') {
      return <MovimientosCB />;
    }
    if (activeTab?.path === '/control-bancario/transferencias') {
      return <TransferenciasCB />;
    }
    if (activeTab?.path === '/control-bancario/extractos') {
      return <ImportarExtractos />;
    }
    if (activeTab?.path === '/control-bancario/conciliacion-automatica') {
      return <ConciliacionAutomatica />;
    }
    if (activeTab?.path === '/control-bancario/conciliacion-manual') {
      return <ConciliacionManual />;
    }
    if (activeTab?.path === '/control-bancario/partidas') {
      return <Partidas />;
    }
    if (activeTab?.path === '/control-bancario/conciliaciones') {
      return <HistorialConciliaciones />;
    }
    if (activeTab?.path === '/control-bancario/chequeras') {
      return <Chequeras />;
    }
    if (activeTab?.path === '/control-bancario/cheques-emision') {
      return <EmisionCheques />;
    }
    if (activeTab?.path === '/control-bancario/cheques-girados') {
      return <ChequesGirados />;
    }
    if (activeTab?.path === '/control-bancario/cheques-posfechados') {
      return <ChequesGirados filtroInicial="posfechados" />;
    }
    if (activeTab?.path === '/control-bancario/cheques-anulacion') {
      return <AnulacionCheques />;
    }
    if (activeTab?.path === '/control-bancario/caja-chica-fondos') {
      return <CajaFondos />;
    }
    if (activeTab?.path === '/control-bancario/caja-chica-vales') {
      return <CajaVales />;
    }
    if (activeTab?.path === '/control-bancario/caja-chica-reposicion') {
      return <CajaReposicion />;
    }
    if (activeTab?.path === '/control-bancario/caja-chica-arqueo') {
      return <CajaArqueo />;
    }
    if (activeTab?.path === '/control-bancario/pagos-programados') {
      return <ProgramacionPagos />;
    }
    if (activeTab?.path === '/contabilidad/tablero-tributario') {
      return <TableroTributario />;
    }
    if (activeTab?.path === '/contabilidad/cuadre') {
      return <CuadreControl />;
    }
    if (activeTab?.path === '/contabilidad/auditoria') {
      return <AuditoriaContinua />;
    }
    if (activeTab?.path === '/seguridad/pista-auditoria') {
      return <PistaAuditoria />;
    }
    if (activeTab?.path === '/contabilidad/paquete-auditor') {
      return <PaqueteAuditor />;
    }
    if (activeTab?.path === '/contabilidad/analitica-auditoria') {
      return <AnaliticaAuditoria />;
    }
    if (activeTab?.path === '/contabilidad/excepciones') {
      return <ExcepcionesContables />;
    }
    if (activeTab?.path === '/financiero/tipos-cambio') {
      return <TiposCambio />;
    }
    if (activeTab?.path === '/control-bancario/pagos-lote') {
      return <PagosLote />;
    }
    if (activeTab?.path === '/control-bancario/ordenes-pago') {
      return <OrdenesPago />;
    }
    if (activeTab?.path === '/control-bancario/ordenes-aprobar') {
      return <AprobacionPagos />;
    }
    if (activeTab?.path === '/control-bancario/cobros') {
      return <Cobros />;
    }
    if (activeTab?.path === '/control-bancario/flujo-caja') {
      return <FlujoDeCaja />;
    }
    if (activeTab?.path === '/control-bancario/libro-bancos') {
      return <LibroBancos />;
    }
    if (activeTab?.path === '/control-bancario/rep-conciliacion') {
      return <ReporteConciliacion />;
    }
    if (activeTab?.path === '/control-bancario/rep-cheques') {
      return <ReporteCheques />;
    }
    if (activeTab?.path === '/control-bancario/rep-flujo') {
      return <FlujoEfectivo />;
    }
    if (activeTab?.path?.startsWith('/control-bancario/')) {
      return <EnConstruccionCB titulo={activeTab.title} />;
    }
    if (activeTab?.path === '/nomina/dashboard') {
      return <DashboardNomina />;
    }
    if (activeTab?.path === '/nomina/empleados') {
      return <Empleados />;
    }
    if (activeTab?.path === '/nomina/contratos') {
      return <Contratos />;
    }
    if (activeTab?.path === '/nomina/departamentos') {
      return <Departamentos />;
    }
    if (activeTab?.path === '/nomina/cargos') {
      return <CargosNomina />;
    }
    if (activeTab?.path === '/nomina/novedades') {
      return <Novedades />;
    }
    if (activeTab?.path === '/nomina/anticipos') {
      return <Anticipos />;
    }
    if (activeTab?.path === '/nomina/desprendible') {
      return <Desprendible />;
    }
    if (activeTab?.path === '/nomina/prestamos') {
      return <Prestamos />;
    }
    if (activeTab?.path === '/nomina/partes-labor') {
      return <PartesLabor />;
    }
    if (activeTab?.path === '/nomina/parametros') {
      return <ParametrosNomina />;
    }
    if (activeTab?.path === '/nomina/rubros') {
      return <Rubros />;
    }
    if (activeTab?.path === '/nomina/rol') {
      return <GenerarRol />;
    }
    if (activeTab?.path === '/nomina/roles') {
      return <HistorialRoles />;
    }
    if (activeTab?.path === '/nomina/rol-semanal') {
      return <RolSemanal />;
    }
    if (activeTab?.path === '/nomina/rol-bisemanal') {
      return <RolBisemanal />;
    }
    if (activeTab?.path === '/nomina/pagos') {
      return <PagosNomina />;
    }
    if (activeTab?.path === '/nomina/decimo-tercero') {
      return <DecimoTercero />;
    }
    if (activeTab?.path === '/nomina/decimo-cuarto') {
      return <DecimoCuarto />;
    }
    if (activeTab?.path === '/nomina/utilidades') {
      return <Utilidades />;
    }
    if (activeTab?.path === '/nomina/fondos-reserva') {
      return <FondosReserva />;
    }
    if (activeTab?.path === '/nomina/vacaciones') {
      return <Vacaciones />;
    }
    if (activeTab?.path === '/nomina/liquidaciones') {
      return <Liquidaciones />;
    }
    if (activeTab?.path === '/nomina/planillas-iess') {
      return <PlanillasIess />;
    }
    if (activeTab?.path === '/nomina/prestamos-iess') {
      return <PrestamosIess />;
    }
    if (activeTab?.path === '/nomina/impuesto-renta') {
      return <ImpuestoRenta />;
    }
    if (activeTab?.path === '/nomina/rep-rol-general') {
      return <RepRolGeneral />;
    }
    if (activeTab?.path === '/nomina/rep-provisiones') {
      return <RepProvisiones />;
    }
    if (activeTab?.path === '/nomina/rep-costo-laboral') {
      return <RepCostoLaboral />;
    }
    if (activeTab?.path === '/nomina/solicitudes') {
      return <SolicitudesPortal />;
    }
    if (activeTab?.path === '/nomina/smtp') {
      return <SmtpNomina />;
    }
    if (activeTab?.path === '/nomina/turnos') {
      return <Turnos />;
    }
    if (activeTab?.path === '/nomina/asistencia') {
      return <Asistencia />;
    }
    if (activeTab?.path === '/nomina/licencias') {
      return <Licencias />;
    }
    if (activeTab?.path === '/nomina/feriados') {
      return <Feriados />;
    }
    if (activeTab?.path === '/nomina/arco') {
      return <SolicitudesArco />;
    }
    if (activeTab?.path === '/nomina/ajustes') {
      return <Ajustes />;
    }
    if (activeTab?.path === '/nomina/ayuda') {
      return <AyudaNomina />;
    }
    if (activeTab?.path === '/portal-empleado/roles') {
      return <MisRoles />;
    }
    if (activeTab?.path === '/portal-empleado/vacaciones') {
      return <MisVacaciones />;
    }
    if (activeTab?.path === '/portal-empleado/solicitudes') {
      return <MisSolicitudes />;
    }
    if (activeTab?.path === '/portal-empleado/datos') {
      return <MisDatos />;
    }
    if (activeTab?.path === '/portal-empleado/asistencia') {
      return <MiAsistencia />;
    }
    if (activeTab?.path?.startsWith('/nomina/') || activeTab?.path?.startsWith('/portal-empleado/')) {
      return <EnConstruccion titulo={activeTab.title} />;
    }
    if (activeTab?.path === '/inventario/recepcion') {
      return <Recepcion />;
    }
    if (activeTab?.path === '/inventario/transferencias') {
      return <Transferencias />;
    }
    if (activeTab?.path === '/inventario/transferencias-config') {
      return <TransferenciasConfig />;
    }
    if (activeTab?.path === '/inventario/ajustes') {
      return <AjusteInventario />;
    }
    if (activeTab?.path === '/inventario/carga-inicial') {
      return <CargaInicial />;
    }
    if (activeTab?.path === '/inventario/reempaque') {
      return <Reempaque />;
    }
    if (activeTab?.path === '/inventario/documentos') {
      return <Documentos />;
    }
    if (activeTab?.path === '/inventario/kardex') {
      return <Kardex />;
    }
    if (activeTab?.path === '/inventario/valorizacion') {
      return <Valorizacion />;
    }
    if (activeTab?.path === '/inventario/stock') {
      return <StockActual />;
    }
    if (activeTab?.path === '/inventario/caducidades') {
      return <Caducidades />;
    }
    if (activeTab?.path === '/inventario/bajo-minimo') {
      return <BajoMinimo />;
    }
    if (activeTab?.path === '/medical/pacientes') {
      return <Pacientes />;
    }
    if (activeTab?.path === '/medical/duplicados') {
      return <PacientesDuplicados />;
    }
    if (activeTab?.path === '/medical/medicos') {
      return <Medicos />;
    }
    if (activeTab?.path === '/medical/especialidades') {
      return <Especialidades />;
    }
    if (activeTab?.path === '/medical/agenda') {
      return <Agenda />;
    }
    if (activeTab?.path === '/medical/lista-espera') {
      return <ListaEspera />;
    }
    if (activeTab?.path === '/medical/historia') {
      return <HistoriaClinica />;
    }
    if (activeTab?.path === '/medical/plantillas') {
      return <Plantillas />;
    }
    if (activeTab?.path === '/medical/convenios') {
      return <Convenios />;
    }
    if (activeTab?.path === '/medical/aptitud') {
      return <Aptitud />;
    }
    if (activeTab?.path === '/medical/derivaciones') {
      return <ReporteDerivaciones />;
    }
    if (activeTab?.path === '/medical/recetas') {
      return <Recetas />;
    }
    if (activeTab?.path === '/medical/certificados') {
      return <Certificados />;
    }
    if (activeTab?.path === '/medical/mi-clinica') {
      return <MiClinica />;
    }
    if (activeTab?.path === '/medical/examenes') {
      return <Examenes />;
    }
    if (activeTab?.path === '/medical/laboratorio') {
      return <Laboratorio />;
    }
    if (activeTab?.path === '/medical/teleconsulta') {
      return <Teleconsulta />;
    }
    if (activeTab?.path === '/medical/mensajes') {
      return <Mensajes />;
    }
    if (activeTab?.path === '/medical/solicitudes-receta') {
      return <SolicitudesReceta />;
    }
    if (activeTab?.path === '/medical/recursos') {
      return <Recursos />;
    }
    if (activeTab?.path === '/medical/calendario') {
      return <CalendarioCorp />;
    }
    if (activeTab?.path === '/medical/design') {
      return <DesignShowcase />;
    }
    if (activeTab?.path === '/medical/ayuda') {
      return <AyudaMedical />;
    }

    if (activeTab?.path === '/medical/ayuda-telemedicina') {
      return <AyudaTelemedicina />;
    }

    if (activeTab?.path === '/medical/ayuda-reclamos') {
      return <AyudaReclamosAseguradora />;
    }

    if (activeTab?.path === '/medical/consulta-reclamos') {
      return <ConsultaReclamos />;
    }

    if (activeTab?.path === '/medical/dashboard') {
      return <DashboardMedico />;
    }
    if (activeTab?.path === '/medical/consentimientos') {
      return <Consentimientos />;
    }
    if (activeTab?.path === '/medical/canales') {
      return <CanalesMedicos />;
    }
    if (activeTab?.path === '/medical/visitantes') {
      return <Visitantes />;
    }
    if (activeTab?.path === '/portal/inicio') {
      return <PortalPaciente />;
    }

    // Pantallas de administración (reemplazan al antiguo CRUD automático)
    if (activeTab?.path === '/seguridad/paises') {
      return <Paises />;
    }
    if (activeTab?.path === '/seguridad/empresas') {
      return <Empresas />;
    }
    if (activeTab?.path === '/seguridad/usuarios') {
      return <Usuarios />;
    }
    if (activeTab?.path === '/seguridad/perfiles') {
      return <Perfiles />;
    }
    if (activeTab?.path === '/sistemas/procesos') {
      return <Procesos />;
    }
    if (activeTab?.path === '/seguridad/menus') {
      return <Menus />;
    }
    if (activeTab?.path === '/seguridad/submenus') {
      return <Submenus />;
    }
    if (activeTab?.path === '/seguridad/opciones') {
      return <Opciones />;
    }
    if (activeTab?.path === '/seguridad/privacidad') {
      return <AvisoPrivacidad />;
    }
    if (activeTab?.path === '/seguridad/brechas') {
      return <Brechas />;
    }
    if (activeTab?.path === '/seguridad/tratamiento') {
      return <RegistroTratamiento />;
    }
    if (activeTab?.path === '/seguridad/retencion') {
      return <RetencionDatos />;
    }
    if (activeTab?.path === '/seguridad/dpo') {
      return <Delegado />;
    }
    if (activeTab?.path === '/seguridad/sql') {
      return <ConsolaSQL />;
    }
    if (activeTab?.path === '/financiero/partner') {
      return <Partner />;
    }
    if (activeTab?.path === '/financiero/retencion-codigos') {
      return <RetencionCodigos />;
    }
    if (activeTab?.path === '/financiero/categorias') {
      return <CategoriasDocumento />;
    }

    // Control de ganado (esquema ganaderia)
    if (activeTab?.path === '/ganaderia/dashboard') {
      return <DashboardGanaderia />;
    }
    if (activeTab?.path === '/ganaderia/hato') {
      return <Hato />;
    }
    if (activeTab?.path === '/ganaderia/movimientos') {
      return <MovimientosGanaderia />;
    }
    if (activeTab?.path === '/ganaderia/predios') {
      return <PrediosPotreros />;
    }
    if (activeTab?.path === '/ganaderia/lotes') {
      return <Lotes />;
    }
    if (activeTab?.path === '/ganaderia/sanidad') {
      return <SanidadGanaderia />;
    }
    if (activeTab?.path === '/ganaderia/reproduccion') {
      return <ReproduccionGanaderia />;
    }
    if (activeTab?.path === '/ganaderia/leche') {
      return <LecheGanaderia />;
    }
    if (activeTab?.path === '/ganaderia/costos') {
      return <CostosGanaderia />;
    }
    if (activeTab?.path === '/ganaderia/ayuda') {
      return <AyudaGanaderia />;
    }

    // Pantallas propias de la marca de la instalación (TrackConstruction: Jobs…)
    const PantallaMarca = activeTab?.path ? marca.pantallas?.[activeTab.path] : undefined;
    if (PantallaMarca) return <PantallaMarca />;

    // Cuenta del usuario (disponible para cualquier usuario logueado, sin
    // depender del menú/perfil ni de nivel de acceso)
    if (activeTab?.path === '/mi-cuenta') {
      return <MiCuenta />;
    }

    // Rutas estáticas
    switch (activeTab.id) {
      case 'permission-assignment':
        return <PermissionAssignment />;
      default:
        return <EnConstruccion titulo={activeTab.title} />;
    }
  };

  const contenido = (
    <>
      {/* Keep-alive: cada pestaña abierta se monta una sola vez y se mantiene viva;
          las inactivas se ocultan (display:none) en lugar de desmontarse, para que
          conserven su estado (formularios, scroll, datos) al navegar entre pestañas. */}
      <AvisoPrivacidadGate>
      {tabs.length === 0 ? (
        (menusLoading || primeraOpcion) ? (
          <div className="flex h-full items-center justify-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <SinModulos />
        )
      ) : (
        tabs.map((tab) => (
          <div key={tab.id} hidden={tab.id !== activeTabId} className="h-full">
            {renderContentForTab(tab)}
          </div>
        ))
      )}
      </AvisoPrivacidadGate>
    </>
  );

  if (modoApp) {
    return (
      <ModoAppShell empresa={menusData?.empresa?.nombre ?? ''} opciones={opcionesApp}>
        {contenido}
      </ModoAppShell>
    );
  }
  return <AppShell>{contenido}</AppShell>;
}


function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Si hay un tenant recordado en este dispositivo, vuelve a /login/<slug>
      // para conservar la marca de la empresa en vez del login pelado.
      const slug = loginSlugStore.get();
      navigate(slug ? `/login/${slug}` : '/login', { replace: true, state: { from: location } });
    }
  }, [isLoading, isAuthenticated, navigate, location]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (user?.mustChangePassword) {
    return <CambiarClaveGate />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Ruta pública - Login (pelada y con slug de empresa para branding) */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/login/:slug"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Recuperar contraseña — pública, por token del enlace enviado por correo */}
      <Route
        path="/reset-clave/:token"
        element={
          <PublicRoute>
            <ResetClave />
          </PublicRoute>
        }
      />

      {/* Rutas protegidas */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppContent />
          </ProtectedRoute>
        }
      />

      {/* Cartelería TV (kiosko público por token, sin login) */}
      <Route path="/tv/:token" element={<TvSignage />} />
      <Route path="/verificar-contrato/:codigo" element={<VerificarContrato />} />
      <Route path="/verificar-contrato-crm/:codigo" element={<VerificarContratoCrm />} />
      <Route path="/rx/:codigo" element={<VerificarReceta />} />
      <Route path="/px/:codigo" element={<VerificarPedido />} />
      <Route path="/carta/:token" element={<CartaQr />} />
      <Route path="/signage/tv/:token" element={<SignageTV />} />
      <Route path="/signage/cert/:folio" element={<CertificadoSignage />} />
      <Route path="/signage/pair" element={<SignagePair />} />

      {/* Andón de cocina en TV (kiosko público por token, sin login) */}
      <Route path="/cocina/:token" element={<AndonTv />} />
      <Route path="/reparto/:token" element={<RepartoApp />} />
      <Route path="/kiosco/:token" element={<Kiosco />} />
      <Route path="/comedor/:token" element={<Comedor />} />
      <Route path="/cadena/:despachoId" element={<CadenaCert />} />

      {/* Storefront e-commerce público por slug (sub-rutas internas) */}
      <Route path="/tienda/:slug/*" element={<TiendaPublica />} />
      <Route path="/pitstop/:token" element={<PitStop />} />
      <Route path="/pasaporte/:token" element={<Pasaporte />} />
      <Route path="/verificar-bench/:codigo" element={<VerificarBench />} />
      <Route path="/taller-tv/:token" element={<TvTaller />} />

      {/* Redirigir rutas desconocidas al home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Si el refresh token expira/inválida, el cliente API emite 'auth:expired';
  // limpiamos el usuario en caché para que las rutas protegidas redirijan a login.
  useEffect(() => {
    const onExpired = () => {
      queryClient.setQueryData(['currentUser'], null);
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        {/* VoiceFormProvider: las pantallas registran su formulario para el dictado estructurado */}
        <VoiceFormProvider>
          <AppRoutes />

          {/* Asistente Virtual - Disponible globalmente */}
          <VirtualAssistant />

          {/* Orbe de dictado por voz (se muestra solo si la empresa lo activó) */}
          <VoiceOrb />
        </VoiceFormProvider>

        {/* Toast notifications */}
        <Toaster position="top-right" richColors />

        {/* PWA: aviso "nueva versión disponible" con actualización de 1 clic (registerType: 'prompt') */}
        <PwaUpdater />

        {/* React Query Devtools (solo en desarrollo) */}
        <ReactQueryDevtools initialIsOpen={false} />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
